// Ported from roammyway_funnel's authFormPrimitives.tsx -- same classNames
// (see src/index.css for the matching brand-* tokens), rebuilt as plain
// template strings instead of class-variance-authority to avoid a new dep.

const BASE_SOCIAL =
  'inline-flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 text-[15px] font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 lg:h-[46px] lg:gap-2.5 lg:text-sm';

export function socialButtonVariants({ tone }) {
  const toneClass =
    tone === 'facebook'
      ? 'border-transparent bg-[#1877F2] text-white hover:bg-[#166FE5] focus-visible:ring-[#1877F2] lg:active:bg-[#125fc7]'
      : 'border-brand-border bg-white text-brand-blue hover:bg-brand-grey focus-visible:ring-brand-teal-strong lg:active:bg-brand-border';
  return `${BASE_SOCIAL} ${toneClass}`;
}

export function fieldInputVariants({ invalid, filled }) {
  const base =
    'h-[50px] w-full rounded-xl border bg-white px-4 font-brand text-[15px] text-brand-blue shadow-sm outline-none transition-colors placeholder:text-brand-text-secondary/70 focus:border-transparent focus:ring-2 disabled:cursor-not-allowed disabled:bg-brand-grey disabled:text-brand-text-secondary lg:h-12 lg:text-[14.5px]';
  const invalidClass = invalid
    ? 'border-brand-error focus:ring-brand-error'
    : 'border-brand-border focus:ring-brand-teal-strong';
  const filledClass = !invalid && filled ? 'border-brand-blue/30' : '';
  return `${base} ${invalidClass} ${filledClass}`;
}

export function primaryButtonVariants({ tone = 'idle' } = {}) {
  const base =
    'inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl border border-transparent text-[15px] font-semibold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 lg:h-12 lg:text-sm';
  const toneClass =
    tone === 'success'
      ? 'bg-brand-success'
      : 'bg-brand-teal-strong hover:bg-brand-blue focus:ring-brand-teal-strong lg:active:bg-[#123449]';
  return `${base} ${toneClass}`;
}

export function Spinner({ className }) {
  return (
    <svg
      className={`h-4 w-4 animate-spin ${className || ''}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function ErrorBanner({ message }) {
  return (
    <div className="rounded-xl border border-brand-error/30 bg-brand-error/5 p-3 font-brand text-sm text-brand-error" role="alert" aria-live="polite">
      <div className="flex items-start gap-2">
        <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
        <span>{message}</span>
      </div>
    </div>
  );
}

export function GoogleIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 01-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.88-3c-1.08.72-2.45 1.15-4.05 1.15-3.11 0-5.75-2.1-6.69-4.92H1.3v3.09A12 12 0 0012 24z" />
      <path fill="#FBBC05" d="M5.31 14.32a7.2 7.2 0 010-4.64V6.59H1.3a12 12 0 000 10.82l4.01-3.09z" />
      <path fill="#EA4335" d="M12 4.75c1.76 0 3.35.61 4.6 1.8l3.45-3.45C17.94 1.19 15.24 0 12 0A12 12 0 001.3 6.59l4.01 3.09C6.25 6.86 8.89 4.75 12 4.75z" />
    </svg>
  );
}

export function FacebookIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z" />
    </svg>
  );
}
